import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDeleteModal from "./confirmDeleteModal";
import Toast from "..//../components/toast";
import ToastContainer from "../../components/toastContainer";
import { getPromoterById, deletePromoter, getPromoters, updatePromoter } from "../../api/promotersApi";
import { TrashIcon } from "@heroicons/react/outline";
import PageLoader from "../../components/pageLoader";
import PromoterFormModal from "./PromoterFormModal";

const TABS = [
  { key: "main", label: "Main Info" },
  { key: "bookings", label: "Bookings" },
  { key: "documents", label: "Documents" },
  { key: "technical", label: "Technical Riders" },
];

const PromoterDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [promoter, setPromoter] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("main");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [
      address.address_1,
      address.address_2,
      address.address_3,
      address.city,
      address.county,
      address.country,
      address.postcode,
    ];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  useEffect(() => {
    const fetchPromoter = async () => {
      try {
        setLoading(true);
        const data = await getPromoterById(id);
        setPromoter(data);
      } catch (err) {
        setToast({
          type: "error",
          message: err?.response?.data?.detail,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPromoter();
  }, [id]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deletePromoter(id);

      setToast({
        type: "success",
        message: "Promoter deleted successfully",
      });

      setTimeout(() => {
        navigate("/dashboard/promoters", { replace: true });
      }, 1000);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleUpdatePromoter = async (formData) => {
    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        company_name: formData.company_name,
        company_id: formData.company_id,
        company_reg_no: formData.company_reg_no,
        company_vat_no: formData.company_vat_no,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        closest_airports: formData.closestAirports.map((name) => ({ name })),
        address: {
          address_1: formData.addressLine1,
          address_2: formData.addressLine2,
          address_3: formData.addressLine3,
          city: formData.city,
          county: formData.county,
          country: formData.country,
          postcode: formData.postcode,
        },
        venue: {
          name: formData.venue_name,
          address: {
            address_1: formData.venue_addressLine1,
            address_2: formData.venue_addressLine2,
            address_3: formData.venue_addressLine3,
            city: formData.venue_city,
            county: formData.venue_county,
            country: formData.venue_country,
            postcode: formData.venue_postcode,
          },
        },
      };

      await updatePromoter(id, payload);

      setToast({
        type: "success",
        message: "Promoter updated successfully",
      });

      setShowEditModal(false);
      getPromoters();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  const Section = ({ title, children }) => (
    <div>
      <h3 className="text-white font-semibold mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
    </div>
  );

  const InfoRow = ({ label, value, full }) => (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-slate-400 mb-1">{label}</p>
      <p className="text-white">{value || "-"}</p>
    </div>
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-400 hover:text-white"
        >
          ← Back to Promoters
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium"
          >
            Edit Promoter
          </button>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Title */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Promoter Details</h1>
        <p className="text-sm text-slate-400">Promoter ID: {id}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 flex gap-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-3 text-sm font-medium transition
              ${
                activeTab === tab.key
                  ? "text-pink-500 border-b-2 border-pink-500"
                  : "text-slate-400 hover:text-white"
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl p-6">
        {/* Main Info */}
        {activeTab === "main" && (
          <div className="space-y-8 text-sm">
            {/* Company Information */}
            <Section title="General Information">
              <InfoRow label="Promoter Name" value={promoter.name} />
              <InfoRow label="Company Name" value={promoter.company_name} />
              <InfoRow
                label="Company Registration No"
                value={promoter.company_reg_no}
              />
              <InfoRow label="Company ID" value={promoter.company_id} />
              <InfoRow label="Company VAT No" value={promoter.company_vat_no} />
              <InfoRow
                label="Address"
                value={formatAddress(promoter.address)}
                full
              />
            </Section>

            {/* Venue Information */}
            <Section title="Venue Information">
              <InfoRow label="Venue Name" value={promoter.venue.name} />
              <InfoRow
                label="Venue Full Address"
                value={formatAddress(promoter.venue.address)}
                full
              />
            </Section>

            {/* Contact Information */}
            <Section title="Promoter Contact">
              <InfoRow label="Contact Name" value={promoter.contact_name} />
              <InfoRow label="Contact Email" value={promoter.contact_email} />
              <InfoRow
                label="Contact Phone No"
                value={promoter.contact_phone}
              />
            </Section>

            {/* Travel Information */}
            <Section title="Travel Information">
              <InfoRow
                label="Closest Airports"
                value={promoter.closest_airports?.map((a) => a.name).join(", ")}
                full
              />
            </Section>
          </div>
        )}

        {/* Bookings */}
        {activeTab === "bookings" && (
          <div className="text-slate-400 text-sm">
            Bookings content will be added here.
          </div>
        )}

        {/* Documents */}
        {activeTab === "documents" && (
          <div className="text-slate-400 text-sm">
            Documents content will be added here.
          </div>
        )}

        {/* Technical Riders */}
        {activeTab === "technical" && (
          <div className="text-slate-400 text-sm">
            Technical riders information will be added here.
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        title="Delete Promoter"
        description="Are you sure you want to delete this promoter? This action cannot be undone."
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={deleting}
      />

      <PromoterFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdatePromoter}
        loading={saving}
        isEdit
        initialData={promoter}
      />

      {/* Toast */}
      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default PromoterDetailsPage;
